import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Calendar, Lightning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function CzechiaWatch() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API}/worldcup/czechia-watch`)
            .then((r) => setData(r.data))
            .catch(() => { /* silent — widget hides if no data */ })
            .finally(() => setLoading(false));
    }, []);

    if (loading || !data?.available) return null;

    const team = data.team;
    const nextFixture = (data.fixtures || []).find((f) => f.status !== "FINISHED") || data.fixtures?.[0];
    const isHome = nextFixture?.home_team_id === team.fd_team_id;
    const opponent = isHome ? nextFixture?.away_team_name : nextFixture?.home_team_name;

    return (
        <section
            className="border-2 bg-gradient-to-r from-[#11457e]/30 via-[#0d0d0d] to-[#d7141a]/30 p-5 space-y-4"
            style={{ borderColor: "#d7141a40" }}
            data-testid="czechia-watch"
        >
            <header className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2" style={{ color: "#d7141a" }}>
                        <Heart size={11} weight="fill" />
                        Czechia Watch
                    </div>
                    <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-1">
                        🇨🇿 Naši na MS 2026
                    </h2>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                    {(team.group || "").replace("GROUP_", "Group ")} · Coach: {team.coach || "—"}
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="czechia-cards">
                {/* Next match */}
                {nextFixture && (
                    <div className="border border-white/10 bg-black/40 p-3" data-testid="czechia-next-match">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                            <Calendar size={10} weight="fill" /> Next match
                        </div>
                        <div className="font-display font-black text-xl mt-1">
                            vs {opponent || "?"}
                            <span className="text-white/40 font-mono text-[12px] ml-1.5">({isHome ? "H" : "A"})</span>
                        </div>
                        <div className="font-mono text-[10px] text-white/60 mt-1">
                            {fmtKick(nextFixture.utc_date)}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1">
                            MD{nextFixture.matchday}
                        </div>
                    </div>
                )}

                {/* PL crossref */}
                <div className="border border-white/10 bg-black/40 p-3" data-testid="czechia-pl-players">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint">
                        🇬🇧 PL crossref · {data.pl_player_count} players
                    </div>
                    {(data.pl_top_players || []).length > 0 ? (
                        <ul className="mt-2 space-y-1 font-mono text-[11px]">
                            {data.pl_top_players.slice(0, 4).map((p) => {
                                const pv = p.pl_value || {};
                                return (
                                    <li key={p.fd_player_id} className="flex items-center justify-between">
                                        <span className="text-white truncate">{p.name}</span>
                                        <span className="text-fa-mint text-[9px] uppercase tracking-widest">
                                            {pv.value_rank ? `#${pv.value_rank}` : "in PL"} · {pv.fpl_team_name?.slice(0, 12)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="font-mono text-[10px] text-white/40 italic mt-2">No PL players in squad.</div>
                    )}
                </div>

                {/* Group context */}
                <div className="border border-white/10 bg-black/40 p-3" data-testid="czechia-group-table">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream">
                        {(team.group || "").replace("GROUP_", "Group ")} standings
                    </div>
                    {data.group_table ? (
                        <ul className="mt-2 space-y-1 font-mono text-[11px]">
                            {data.group_table.map((r) => (
                                <li key={r.position} className={`flex items-center justify-between ${r.team_id === team.fd_team_id ? "text-fa-yellow" : "text-white/60"}`}>
                                    <span>{r.position}. {r.team_name}</span>
                                    <span className="font-display font-black">{r.points} pts</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="font-mono text-[10px] text-white/40 italic mt-2">Group hasn't started.</div>
                    )}
                </div>
            </div>

            <Link
                to={`/worldcup/team/${team.fd_team_id}`}
                className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white"
                data-testid="czechia-team-link"
            >
                Full squad · all fixtures →
            </Link>
            {nextFixture && (
                <button
                    type="button"
                    onClick={async () => {
                        setGenerating(true);
                        try {
                            const r = await axios.post(`${API}/worldcup/match-call/generate?match_id=${nextFixture.fd_match_id}`);
                            toast.success(`Český breakdown ready · ${r.data.players.length} karet`);
                            navigate(`/play/market/${r.data.id}`);
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Generování selhalo");
                            setGenerating(false);
                        }
                    }}
                    disabled={generating}
                    className="ml-3 inline-flex items-center gap-1.5 bg-fa-yellow text-black font-display font-black uppercase tracking-wide text-[11px] px-3 py-1.5 hover:bg-fa-yellow/90 disabled:opacity-40"
                    data-testid="czechia-breakdown-btn"
                >
                    <Lightning size={10} weight="fill" />
                    {generating ? "Generuji…" : "🇨🇿 Český breakdown"}
                </button>
            )}
        </section>
    );
}
